import { z } from 'zod';
import type { Role } from './enums';

/**
 * Permission catalogue — the single source for the API guard (`@RequirePerm`), the DB seed and
 * the UI (`<Can>`, menu visibility). ADR 0004: `finance.view` only for owner/admin/manager.
 */
export const PERMISSIONS = [
  'dashboard.view',
  'projects.view',
  'projects.edit',
  'notes.edit',
  'customers.view',
  'customers.edit',
  'tasks.view',
  'tasks.edit',
  'resources.view',
  'resources.edit',
  'technologies.view',
  'technologies.edit',
  'timesheets.view',
  'timesheets.edit',
  'finance.view',
  'support.view',
  'support.edit',
  'overview.view',
  'reconciliation.manage',
  'bizcycle.view',
  'bizcycle.edit',
  'ai.run',
  'export.run',
  'members.view',
  'members.manage',
  'integrations.manage',
  'apitokens.manage',
  'workspace.delete',
] as const;

export type Permission = (typeof PERMISSIONS)[number];
export const permissionSchema = z.enum(PERMISSIONS);

const except = (...excluded: Permission[]): Permission[] => PERMISSIONS.filter((p) => !excluded.includes(p));

const READ_ONLY: Permission[] = PERMISSIONS.filter((p) => p.endsWith('.view') && p !== 'finance.view');

export const ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = {
  owner: PERMISSIONS,
  admin: except('workspace.delete'),
  manager: except('workspace.delete', 'members.manage', 'integrations.manage'),
  member: [...READ_ONLY, 'tasks.edit', 'notes.edit', 'apitokens.manage'],
  viewer: [...READ_ONLY, 'apitokens.manage'],
};

export function permissionsFor(role: Role): readonly Permission[] {
  return ROLE_PERMISSIONS[role];
}

export function hasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/** Roles whose members may perform write operations via API tokens with `write` scope. */
export function canWrite(role: Role): boolean {
  return role !== 'viewer';
}
